import React, { useEffect, useState } from 'react';
import {
  Alert,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  IconButton,
  Box,
  Button,
  Snackbar,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon  from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import Swal from 'sweetalert2';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

// Project import
import { gridSpacing } from 'config.js';

const getToken = () => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Token not found");
  }
  return token;
};

const CareerPage = () => {
  const [data, setData] = useState([]);
  const [buMasters, setBuMasters] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('error');

  const navigate = useNavigate();

  // Fetch data from APIs
  const fetchData = async () => {
    const token = getToken();
    try {
      const response = await axios.get('https://3wzg6m6x-5000.asse.devtunnels.ms/api/career', {
        headers: { Authorization: `${token}` },
      });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching career data:', error);
    }
  };

  const fetchBuMasters = async () => {
    const token = getToken();
    try {
      const response = await axios.get('https://3wzg6m6x-5000.asse.devtunnels.ms/api/bu_master', {
        headers: { Authorization: `${token}` },
      });
      setBuMasters(response.data);
    } catch (error) {
      console.error('Error fetching business units:', error);
    }
  };

  useEffect(() => {
    fetchData();
    fetchBuMasters();
  }, []);

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteClick = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    }).then((result) => {
      if (result.isConfirmed) {
        handleDeleteCareer(id);
      }
    });
  };

  const handleDeleteCareer = async (id) => {
    const token = getToken();
    try {
      await axios.delete(`https://3wzg6m6x-5000.asse.devtunnels.ms/api/career/delete/${id}`, {
        headers: {           
          Authorization: `Bearer ${token}`
      },
      });
      setData(data.filter(career => career.id !== id));
      Swal.fire({
        icon: 'success',
        title: 'Career deleted successfully!',
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      console.error('Error deleting career:', error);
    }
  };

  const filteredData = data.filter(career =>
    career.title.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <>
      <Grid container spacing={gridSpacing}>
        <Grid item xs={12}>
          <Card sx={{ borderRadius: '12px', boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.1)' }}>
            <CardHeader
              title={
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="h5" style={{ color: '#2d3436', fontFamily: 'Poppins' }}>
                    Career Management
                  </Typography>
                  <Box>
                    <TextField
                      placeholder="Search..."
                      variant="outlined"
                      size="small"
                      value={searchText}
                      onChange={handleSearchChange}
                      sx={{ marginRight: 2, borderRadius: '8px', backgroundColor: '#f5f5f5' }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={() => navigate('/compro/career/add')}
                    >
                      Add Career
                    </Button>
                  </Box>
                </Box>
              }
            />
            <Divider />
            <CardContent>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>No</TableCell>
                      <TableCell>Title</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Deadline</TableCell>
                      <TableCell>Business Unit</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
  {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((career, index) => {
    // Get the current date
    const currentDate = new Date();
    // Get the deadline date
    const deadlineDate = new Date(career.deadline);
    // Check if the current date is past the deadline
    const isPastDeadline = currentDate > deadlineDate;

    return (
      <TableRow key={career.id}>
        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
        <TableCell>
          <Box display="flex" alignItems="center">
          <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: isPastDeadline ? 'red' : career.is_active ? 'green' : 'gray', 
              }}
            />
            <Typography variant="body1" sx={{ marginLeft: 1 }}>
              {career.title}
            </Typography>
          </Box>
        </TableCell>
        <TableCell>{career.location}</TableCell>
        <TableCell>{career.type}</TableCell>
        <TableCell>{new Date(career.deadline).toLocaleDateString()}</TableCell>
        <TableCell>{career.bu_master.name}</TableCell>
        <TableCell>
          <IconButton onClick={() => navigate(`/compro/career/view/${career.id}`)}>
            <VisibilityIcon />
          </IconButton>
          <IconButton onClick={() => navigate(`/compro/career/edit/${career.id}`)}>
            <EditIcon />
          </IconButton>
          <IconButton onClick={() => handleDeleteClick(career.id)}>
            <DeleteIcon />
          </IconButton>
        </TableCell>
      </TableRow>
    );
  })}
</TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert severity={snackbarSeverity} onClose={() => setSnackbarOpen(false)}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CareerPage;